import { FadeUp, Eyebrow, usePageTitle, LineReveal } from "@/components/Motion";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const TEAM = [
  { name: "Haad Bakshi", role: "CEO & Producer", bio: "Founder of Incolour Films — director, producer, and cinematographer across the studio's films, commercials, and music videos.", img: "https://cdn.sanity.io/images/ivs65dh9/production/d4a2b1e97328bffbb4b7256cdaa7d7bd4ed0604b-800x800.jpg?w=800&h=800&fit=crop&auto=format" },
  { name: "Zehra Abrar", role: "Executive Producer & Lawyer", bio: "Guides productions from financing through contracts — keeping the studio's deals as clean as its frames.", img: "/logo-mark.webp" },
  { name: "Lee Nisar", role: "Producer & Director", bio: "Produces and directs across the narrative slate, from first treatment to final cut.", img: "/logo-mark.webp" },
  { name: "Moiz Mufti", role: "Business Development & Accounts", bio: "First call for new productions — and the steady hand on budgets and accounts that keeps every shoot moving.", img: "/logo-mark.webp" },
  { name: "Piera Savage", role: "Entertainment Lawyer & Tax Credits", bio: "Production legal, chain of title, and the tax-credit strategy that makes shooting in Canada pay.", img: "/logo-mark.webp" },
  { name: "Aivry Napier", role: "Production Designer", bio: "Builds the worlds — sets, props, and the details that make a frame believable.", img: "/logo-mark.webp" },
];

const CHAPTERS = [
  { n: "01", title: "Narrative-Led, Always", body: "Shorts, PSAs, music videos, brand films — whatever the format, the story comes first. We develop original work with our roster of writer-directors, from first idea to final delivery.", accent: "text-orange" },
  { n: "02", title: "GTA Roots, Global Scope", body: "Made in Toronto and Mississauga, watched everywhere. Our crews carry films, commercials, and music videos from concept through post, all under one roof.", accent: "text-blue" },
  { n: "03", title: "A Studio That's a Community Hub", body: "Between productions we mentor emerging filmmakers and champion diverse storytellers — the studio is a production house and a community, and we intend to keep it that way.", accent: "text-violet" },
];

const PARTNERS = ["ISNA Canada", "Maple Acura", "Bellwoods Coffee & Gelato", "Inhouse", "Harman", "Notifi", "Promethea", "Lie Down Films", "SOTO"];

const FACILITIES = [
  { k: "Narrative", v: "Original shorts and features, developed and produced in-house" },
  { k: "Commercials & PSAs", v: "Brand spots and non-profit campaigns, concept to delivery" },
  { k: "Music Videos", v: "Performance and narrative videos with our directing roster" },
  { k: "Post & Colour", v: "Edit, colour, and finish under one roof" },
];

export default function About() {
  usePageTitle("About");
  return (
    <div className="pt-16" data-testid="about-page">
      <SEO
        title="About Our Toronto Film Production Company"
  description="Incolour Films is a Toronto-based film production company creating original films and providing full-service production for film, television, commercials and branded content."
  path="/about"
      />
      <section className="mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 lg:px-8 lg:pt-24">
        <Eyebrow>About Incolour</Eyebrow>
        <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.0] tracking-tight sm:text-6xl" data-testid="about-title">
          <LineReveal lines={["A Toronto production company", "built like a community."]} />
        </h1>
        <FadeUp delay={0.3}>
          <p className="mt-8 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            Incolour Films was founded in 2019 by filmmaker Haad Bakshi as a home for narrative-led content in the GTA. Today we're a full-service production company and community hub — making original films, and carrying commercials, PSAs, and music videos from concept to final delivery.
          </p>
        </FadeUp>
      </section>

      {/* STORY */}
      <section className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-24">
        <FadeUp>
          <div className="overflow-hidden rounded-2xl border border-border">
            <img
              src="https://images.unsplash.com/photo-1517935706615-2717063c2225?auto=format&fit=crop&w=1600&q=80"
              alt="Toronto skyline at dusk"
              loading="lazy"
              className="aspect-[4/3] w-full object-cover"
            />
          </div>
        </FadeUp>
        <FadeUp delay={0.12} className="flex flex-col justify-center">
          <Eyebrow>The Story</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">From a one-man camera kit to a full-service studio.</h2>
          <p className="mt-6 text-base leading-relaxed text-muted-foreground">
            Incolour started with founder Haad Bakshi shooting wherever the work was — commercials, music videos, and shorts across the GTA. Each project grew the crew, the kit, and the circle of collaborators, until the studio became a production house in its own right.
          </p>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            What hasn't changed is the approach: narrative-led, community-rooted, and hands-on at every stage — development, set, and post.
          </p>
        </FadeUp>
      </section>

      {/* MANIFESTO */}
      <section className="border-y border-border bg-cream2 py-16 dark:bg-card lg:py-24" data-testid="about-manifesto">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Eyebrow>Manifesto</Eyebrow>
          <div className="mt-10 space-y-0">
            {CHAPTERS.map((c, i) => (
              <FadeUp key={c.n} delay={i * 0.1}>
                <div className="group grid gap-4 border-t border-border py-10 transition-colors last:border-b hover:bg-background lg:grid-cols-12 lg:items-baseline lg:px-6">
                  <p className={`font-mono text-5xl font-medium lg:col-span-2 ${c.accent}`}>{c.n}</p>
                  <h3 className="font-display text-2xl font-bold tracking-tight sm:text-3xl lg:col-span-4">{c.title}</h3>
                  <p className="text-base leading-relaxed text-muted-foreground lg:col-span-6">{c.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24" data-testid="team-section">
        <Eyebrow>The People</Eyebrow>
        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">Small team, deep bench.</h2>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {TEAM.map((t, i) => (
            <FadeUp key={t.name} delay={i * 0.08}>
              <div className="group relative overflow-hidden rounded-2xl border border-border" data-testid={`team-member-${t.name.toLowerCase().replace(/\s+/g, "-")}`}>
                <img
                  src={t.img}
                  alt={t.name}
                  loading="lazy"
                  className={`aspect-[4/5] w-full transition-transform duration-700 group-hover:scale-[1.04] ${
                    t.img.startsWith("/") ? "bg-cream2 object-contain p-14 dark:bg-card" : "object-cover"
                  }`}
                />
                <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-ink/90 via-ink/30 to-transparent p-6">
                  <p className="font-display text-xl font-bold text-cream">{t.name}</p>
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-orange">{t.role}</p>
                  <p className="mt-3 max-h-0 overflow-hidden text-sm leading-relaxed text-cream/80 transition-all duration-500 group-hover:max-h-32">
                    {t.bio}
                  </p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* COMMUNITY */}
      <section className="bg-ink py-16 text-cream lg:py-24" data-testid="community-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.22em] text-cream/50">
            <span className="inline-block h-2 w-2 bg-orange" /> Community
          </p>
          <h2 className="mt-4 max-w-3xl font-display text-3xl font-bold tracking-tight sm:text-4xl">
            We make films <span className="font-serif italic text-orange">with</span> the city, not just in it.
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-cream/70">
            From non-profit campaigns with ISNA Canada to videos with independent artists across the GTA — these are some of the clients and collaborators we make work with, year after year.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            {PARTNERS.map((p) => (
              <span key={p} className="rounded-full border border-cream/25 px-5 py-2.5 text-sm text-cream/85 transition-colors hover:border-orange hover:text-orange">
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FACILITIES */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24" data-testid="facilities-section">
        <Eyebrow>Services</Eyebrow>
        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">What we do.</h2>
        <div className="mt-10 grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
          {FACILITIES.map((f) => (
            <div key={f.k} className="bg-background p-7">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-orange">{f.k}</p>
              <p className="mt-3 text-sm leading-relaxed">{f.v}</p>
            </div>
          ))}
        </div>
        <FadeUp delay={0.15} className="mt-12">
          <Link to="/contact" data-testid="about-cta-link" className="inline-flex items-center gap-3 rounded-full bg-ink px-8 py-4 text-sm font-semibold text-cream transition-transform hover:scale-[1.04] dark:bg-cream dark:text-ink">
            Work with us <ArrowUpRight size={16} />
          </Link>
        </FadeUp>
      </section>
    </div>
  );
}
